import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import BfSheet from './BfSheet';
import {
  useReadingSize,
  useBfTheme,
  BF_READING_SIZES,
  BF_THEMES,
  useAmbientMode,
  bfHaptic,
} from './BfHooks';
import { ensureLanguageLoaded, notifyLocaleChanged, t } from '../../../i18n/uiText';
import {
  fetchLanguagesList,
  getDefaultTranslationForLanguage,
  syncBibleLanguageFromTranslation,
} from '../../../utils/bibleLocaleSync';

const API_BASE =
  import.meta.env.VITE_API_URL || 'https://verbum-api-production.up.railway.app/api';

/**
 * BfReadingSettings — премиальная панель reading-настроек.
 * Вкладки: (1) экран — размер, тема, шрифт, фокус; (2) язык и переводы.
 * Хранение состояния — в localStorage через хуки (см. BfHooks).
 */
export default function BfReadingSettings({
  onClose,
  ambientControl, // опциональный { ambient, toggle } из родителя
  onSelectFont,
  currentFont = 'book',
  /** id перевода из URL (например synodal) — для подсветки и смены перевода */
  currentTranslationId,
  /** как handleBfTranslationChange в ChapterView: кроссфейд + navigate */
  onTranslationChange,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [languages, setLanguages] = useState(null);
  const [selectedLanguageCode, setSelectedLanguageCode] = useState(() =>
    typeof window !== 'undefined'
      ? (localStorage.getItem('bibleLanguage') || 'en').toLowerCase()
      : 'en',
  );

  const [size, setSize] = useReadingSize();
  const [theme, setTheme] = useBfTheme();
  /** Вкладка 1 — экран чтения; вкладка 2 — язык и переводы (по требованию UX). */
  const [settingsTab, setSettingsTab] = useState('display');
  const fallbackAmbient = useAmbientMode();
  const { ambient, toggle: toggleAmbient } = ambientControl || fallbackAmbient;

  useEffect(() => {
    let cancelled = false;
    fetchLanguagesList().then((langs) => {
      if (!cancelled) setLanguages(Array.isArray(langs) ? langs : []);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const effectiveTranslationId =
    currentTranslationId ||
    (typeof window !== 'undefined' ? localStorage.getItem('bibleTranslation') : null);

  const owningLang = useMemo(() => {
    if (!languages?.length) return null;
    const owner = languages.find(
      (lang) =>
        Array.isArray(lang.translations) &&
        lang.translations.some((tr) => tr.id === effectiveTranslationId),
    );
    if (owner) return owner;
    return languages.find((l) => l.code === selectedLanguageCode) || languages[0] || null;
  }, [languages, effectiveTranslationId, selectedLanguageCode]);

  const navigateToTranslation = useCallback(
    (translationId) => {
      const parts = location.pathname.split('/').filter(Boolean);
      if (parts.length > 0) {
        parts[0] = translationId;
        navigate(`/${parts.join('/')}`, { replace: true });
      } else {
        navigate(`/${translationId}`, { replace: true });
      }
    },
    [navigate, location.pathname],
  );

  const handleSelectLanguage = useCallback(
    async (lang) => {
      bfHaptic('light');
      await ensureLanguageLoaded(lang.code);
      setSelectedLanguageCode(String(lang.code).toLowerCase());
      if (typeof window !== 'undefined') {
        localStorage.setItem('bibleLanguage', lang.code);
        notifyLocaleChanged();
      }

      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
      if (token) {
        try {
          await fetch(`${API_BASE}/me/preferred-language`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ language: lang.code }),
          });
        } catch (e) {
          console.error('Failed to save preferred language', e);
        }
      }

      const targetTranslation =
        lang.defaultTranslation ||
        (Array.isArray(lang.translations) && lang.translations[0]?.id) ||
        getDefaultTranslationForLanguage(lang.code);

      if (targetTranslation) {
        if (typeof window !== 'undefined') {
          localStorage.setItem('bibleTranslation', targetTranslation);
        }
        if (onTranslationChange) {
          onTranslationChange(targetTranslation);
        } else {
          navigateToTranslation(targetTranslation);
        }
      }
      onClose?.();
    },
    [navigateToTranslation, onClose, onTranslationChange],
  );

  const handleSelectTranslation = useCallback(
    async (trId) => {
      if (!trId || trId === effectiveTranslationId) return;
      bfHaptic('light');
      try {
        await syncBibleLanguageFromTranslation(trId);
        localStorage.setItem('bibleTranslation', trId);
      } catch {
        /* no-op */
      }
      if (onTranslationChange) {
        onTranslationChange(trId);
      } else {
        navigateToTranslation(trId);
      }
      onClose?.();
    },
    [effectiveTranslationId, navigateToTranslation, onClose, onTranslationChange],
  );

  const fonts = [
    { id: 'book', label: 'Literata' },
    { id: 'serif_alt', label: 'Source Serif' },
    { id: 'alegreya', label: 'Alegreya' },
    { id: 'sans', label: 'Inter' },
  ];

  const handleFont = (id) => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem('bibleReadingFont', id);
      window.dispatchEvent(new CustomEvent('verbum:reading-font-change', { detail: { font: id } }));
    } catch {
      /* no-op */
    }
    onSelectFont?.(id);
  };

  return (
    <BfSheet title="Чтение" onClose={onClose} ariaLabel="Reading settings">
      <div className="bf-rs">
        <div
          className="bf-rs-tabs"
          role="tablist"
          aria-label={`${t('readingSettingsTabDisplay')}, ${t('appLanguage')}`}
        >
          <button
            type="button"
            role="tab"
            id="bf-rs-tab-display"
            aria-selected={settingsTab === 'display'}
            aria-controls="bf-rs-panel-display"
            className="bf-rs-tab"
            data-active={settingsTab === 'display' ? 'true' : 'false'}
            onClick={() => {
              bfHaptic('light');
              setSettingsTab('display');
            }}
          >
            {t('readingSettingsTabDisplay')}
          </button>
          <button
            type="button"
            role="tab"
            id="bf-rs-tab-language"
            aria-selected={settingsTab === 'language'}
            aria-controls="bf-rs-panel-language"
            className="bf-rs-tab"
            data-active={settingsTab === 'language' ? 'true' : 'false'}
            onClick={() => {
              bfHaptic('light');
              setSettingsTab('language');
            }}
          >
            {t('appLanguage')}
          </button>
        </div>

        {settingsTab === 'language' && (
          <div
            id="bf-rs-panel-language"
            role="tabpanel"
            aria-labelledby="bf-rs-tab-language"
            className="bf-rs-tabpanel"
          >
            <section className="bf-rs-section">
              <p className="bf-rs-lang-help">{t('settingsLanguageHelp')}</p>
              {languages === null ? (
                <p className="bf-rs-lang-loading" role="status">
                  {t('loading')}
                </p>
              ) : (
                <ul className="bf-rs-lang-list" role="list">
                  {languages.map((lang) => {
                    const activeLang = selectedLanguageCode === String(lang.code).toLowerCase();
                    return (
                      <li key={lang.code} className="bf-rs-lang-list__item">
                        <button
                          type="button"
                          className="bf-rs-lang-row"
                          data-active={activeLang ? 'true' : 'false'}
                          onClick={() => handleSelectLanguage(lang)}
                        >
                          <span className="bf-rs-lang-row__name">{lang.name}</span>
                          {activeLang && (
                            <span className="bf-rs-lang-row__check" aria-hidden="true">
                              ✓
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}

              {owningLang?.translations && owningLang.translations.length > 1 && (
                <div className="bf-rs-trn-block">
                  <div className="bf-rs-trn-label">{t('settingsTranslationsLabel')}</div>
                  <ul
                    className="bf-rs-trn-list"
                    role="listbox"
                    aria-label={t('settingsTranslationsLabel')}
                  >
                    {owningLang.translations.map((tr) => {
                      const activeTr = tr.id === effectiveTranslationId;
                      return (
                        <li key={tr.id}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={activeTr}
                            className="bf-rs-trn-row"
                            data-active={activeTr ? 'true' : 'false'}
                            onClick={() => handleSelectTranslation(tr.id)}
                          >
                            <span className="bf-rs-trn-row__name">
                              {tr.name || tr.shortName || tr.id}
                            </span>
                            {activeTr && (
                              <span className="bf-rs-trn-row__check" aria-hidden="true">
                                ✓
                              </span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </section>
          </div>
        )}

        {settingsTab === 'display' && (
          <div
            id="bf-rs-panel-display"
            role="tabpanel"
            aria-labelledby="bf-rs-tab-display"
            className="bf-rs-tabpanel"
          >
            <section className="bf-rs-section">
              <div className="bf-rs-section__head">
                <div className="bf-eyebrow">Размер текста</div>
                <div className="bf-rs-size-preview" aria-hidden="true">
                  <span style={{ fontSize: 13 }}>Aa</span>
                  <span style={{ fontSize: 21 }}>Aa</span>
                </div>
              </div>
              <div className="bf-rs-size-track" role="group" aria-label="Размер шрифта">
                {BF_READING_SIZES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="bf-rs-size-step"
                    data-active={s === size ? 'true' : 'false'}
                    onClick={() => setSize(s)}
                    aria-label={`${s}px`}
                  >
                    <span className="bf-rs-size-step__dot" />
                  </button>
                ))}
              </div>
              <div className="bf-rs-sample" style={{ fontSize: `${size}px` }}>
                «Ибо так возлюбил Бог мир, что отдал Сына Своего Единородного.»
              </div>
            </section>

            <section className="bf-rs-section">
              <div className="bf-eyebrow">Тема</div>
              <div className="bf-rs-segments">
                {BF_THEMES.map((id) => (
                  <button
                    key={id}
                    type="button"
                    className="bf-chip"
                    data-active={theme === id ? 'true' : 'false'}
                    onClick={() => setTheme(id)}
                  >
                    {id === 'light' ? 'Светлая' : 'Тёмная'}
                  </button>
                ))}
              </div>
            </section>

            <section className="bf-rs-section">
              <div className="bf-eyebrow">Шрифт</div>
              <div className="bf-rs-segments">
                {fonts.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    className="bf-chip"
                    data-active={currentFont === f.id ? 'true' : 'false'}
                    onClick={() => handleFont(f.id)}
                    style={{ fontFamily: 'var(--bf-font-reading)' }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </section>

            <section className="bf-rs-section">
              <button
                type="button"
                className="bf-rs-toggle"
                onClick={toggleAmbient}
                aria-pressed={ambient}
              >
                <span>
                  <div className="bf-rs-toggle__label">Режим фокуса</div>
                  <div className="bf-rs-toggle__hint">
                    Скрывает шапку и подсказки. Двойной тап включает в любой момент.
                  </div>
                </span>
                <span className="bf-rs-switch" data-on={ambient ? 'true' : 'false'}>
                  <span className="bf-rs-switch__knob" />
                </span>
              </button>
            </section>
          </div>
        )}
      </div>
    </BfSheet>
  );
}
